import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearErr, fetchReg, logoutFetch } from "../../../store/authSlice";
import {
  isClinicPortal,
  getPlatformUrl,
  redirectToClinicPortal,
} from "../../../utils/portalHost";
import { PORTALS } from "../../../utils/portalTypes";
import { hasAdminAccess } from "../../../utils/roles";
import AuthClubRegisterFlow from "../Auth/AuthClubRegisterFlow";
import PlatformAuthShell from "../Auth/PlatformAuthShell";
import AuthButtonLink from "../Auth/AuthButtonLink";
import Button from "../../ui/Button";
import Seo from "../../Seo";
import L from "../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../constants/trial";
import a from "../Auth/auth.module.css";

export default function ClinicRegister() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wantNewClub = searchParams.get("new") === "1";
  const redirected = useRef(false);
  const [showRegisterForm, setShowRegisterForm] = useState(wantNewClub);
  const { isAuth, role, err, tenant, load } = useSelector((state) => state.auth);

  const hasExistingClub =
    isAuth && hasAdminAccess(role) && Boolean(tenant?.subdomain);

  useEffect(() => {
    if (isClinicPortal()) {
      window.location.href = getPlatformUrl(
        wantNewClub ? "/register-clinic?new=1" : "/register-clinic",
      );
    }
  }, [wantNewClub]);

  useEffect(() => {
    if (!wantNewClub || showRegisterForm) return;
    if (hasExistingClub) {
      dispatch(logoutFetch());
      setShowRegisterForm(true);
    }
  }, [wantNewClub, hasExistingClub, showRegisterForm, dispatch]);

  useEffect(() => {
    if (redirected.current) return;
    if (!isAuth || !hasAdminAccess(role) || !tenant?.subdomain) return;
    if (hasExistingClub && !showRegisterForm) return;
    redirected.current = true;
    redirectToClinicPortal(tenant.subdomain, PORTALS.admin.app);
  }, [isAuth, role, tenant, showRegisterForm, hasExistingClub]);

  useEffect(() => {
    if (err) {
      const t = setTimeout(() => dispatch(clearErr()), 5000);
      return () => clearTimeout(t);
    }
  }, [err, dispatch]);

  const errorText =
    typeof err === "string" ? err : err?.message || err?.error || null;

  const startNewClubRegistration = () => {
    dispatch(logoutFetch());
    setShowRegisterForm(true);
  };

  if (hasExistingClub && !showRegisterForm) {
    return (
      <>
        <Seo
          noindex
          title={`${L.registerClub} — ${L.productName}`}
          description={`Создайте спортивный клуб и получите ${TRIAL_DAYS} дней демо-доступа: клиенты, группы, журнал и финансы.`}
        />
        <PlatformAuthShell
          backTo="/"
          backLabel="На главную"
          title="У вас уже есть клуб"
          subtitle={`Вы вошли как владелец «${tenant.name || tenant.subdomain}». Чтобы зарегистрировать новую организацию, выйдите и заполните форму с другим email.`}
        >
          <div className={a.actions}>
            <Button
              type="button"
              block
              onClick={() =>
                redirectToClinicPortal(tenant.subdomain, PORTALS.admin.app)
              }
            >
              Открыть кабинет «{tenant.name || tenant.subdomain}»
            </Button>
            <AuthButtonLink
              to="/register-clinic?new=1"
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                startNewClubRegistration();
              }}
            >
              Зарегистрировать другой клуб
            </AuthButtonLink>
          </div>
        </PlatformAuthShell>
      </>
    );
  }

  return (
    <>
      <Seo
        noindex
        title={`${L.registerClub} — ${L.productName}`}
        description={`Создайте спортивный клуб и получите ${TRIAL_DAYS} дней демо-доступа: клиенты, группы, журнал и финансы.`}
      />
      <AuthClubRegisterFlow
        loading={load}
        error={errorText}
        onSubmit={(data) => dispatch(fetchReg(data))}
        backTo="/"
        backLabel="Назад"
        onLogin={() => navigate("/find")}
      />
    </>
  );
}
